import React, { useState } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TablePagination, Select, MenuItem,
  IconButton, Tooltip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Button, CircularProgress, Chip, useMediaQuery
} from "@mui/material";
import { CheckCircle, Cancel, AccessTime, Visibility, Replay, Block } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faIdCard, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import baseURL from "../../config";

const MS_COLORS = {
  primary: '#2D4A3D',
  primaryLight: '#EBF5EE',
  text: '#323130',
  textSecondary: '#605D5C',
  background: '#FFFFFF',
  backgroundLight: '#F3F2F1',
  border: '#E1E1E1',
  hoverBackground: '#F0F0F0',
  white: '#FFFFFF',
  accent: '#107C10',
  red: '#D83B01',
  orange: '#FF8C00',
};

const headerCellSx = { fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text };
const bodyCellSx = { fontSize: "0.8rem", color: MS_COLORS.textSecondary, whiteSpace: "nowrap" };

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', py: 0.5 }}>
    <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary, fontWeight: 500, minWidth: 150 }}>{label}</Typography>
    <Typography variant="body2" sx={{ color: MS_COLORS.text, textAlign: 'right', wordBreak: 'break-word', maxWidth: '55%' }}>{value}</Typography>
  </Box>
);

const FormTypeChip = ({ type }) => {
  const isEvent = type === 'event';
  return (
    <Chip
      icon={<FontAwesomeIcon icon={isEvent ? faCalendarAlt : faIdCard} style={{ fontSize: '10px', marginLeft: '8px' }} />}
      label={isEvent ? 'Event' : 'Membership'}
      size="small"
      sx={{
        backgroundColor: isEvent ? '#e3f2fd' : '#e8f5e9',
        color: isEvent ? '#1565c0' : '#2D4A3D',
        fontWeight: 600, fontSize: '0.72rem',
      }}
    />
  );
};

const ManageMembershipTransactions = ({ applications, onRefresh }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);

  const [retryOpen, setRetryOpen] = useState(false);
  const [retryApp, setRetryApp] = useState(null);
  const [retryLoading, setRetryLoading] = useState(false);

  const [markFailedOpen, setMarkFailedOpen] = useState(false);
  const [markFailedApp, setMarkFailedApp] = useState(null);
  const [markFailedLoading, setMarkFailedLoading] = useState(false);

  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const filtered = applications.filter(a => {
    if (statusFilter !== "all" && a.payment_status !== statusFilter) return false;
    if (typeFilter !== "all" && a.form_type !== typeFilter) return false;
    return true;
  });

  // Detail
  const handleViewDetail = (app) => { setSelectedApp(app); setDetailOpen(true); };

  // Retry
  const handleRetry = (app) => { setRetryApp(app); setRetryOpen(true); };
  const handleRetryConfirm = async () => {
    setRetryLoading(true);
    try {
      const res = await fetch(`${baseURL}/admin/membership-applications/${retryApp.id}/retry`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setRetryOpen(false);
        onRefresh();
      } else {
        toast.error(data.error || 'Retry failed.');
      }
    } catch {
      toast.error('An error occurred while retrying.');
    } finally {
      setRetryLoading(false);
    }
  };

  // Mark Failed
  const handleMarkFailed = (app) => { setMarkFailedApp(app); setMarkFailedOpen(true); };
  const handleMarkFailedConfirm = async () => {
    setMarkFailedLoading(true);
    try {
      const res = await fetch(`${baseURL}/admin/membership-applications/${markFailedApp.id}/mark-failed`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setMarkFailedOpen(false);
        onRefresh();
      } else {
        toast.error(data.error || 'Failed to mark as failed.');
      }
    } catch {
      toast.error('An error occurred.');
    } finally {
      setMarkFailedLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.05)', p: 3, mt: 3 }}>
      <Typography variant="h6" sx={{ color: '#1a1a2e', mb: 3, fontWeight: 800, fontSize: isSmallScreen ? '1.05rem' : '1.2rem', letterSpacing: '-0.3px' }}>
        Membership &amp; Event Applications
      </Typography>

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <Select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(0); }} size="small"
          sx={{ minWidth: 150, '& .MuiSelect-select': { fontSize: '0.875rem', color: MS_COLORS.text } }}>
          <MenuItem value="all">All Types</MenuItem>
          <MenuItem value="membership">Membership</MenuItem>
          <MenuItem value="event">Event</MenuItem>
        </Select>
        <Select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} size="small"
          sx={{ minWidth: 150, '& .MuiSelect-select': { fontSize: '0.875rem', color: MS_COLORS.text } }}>
          <MenuItem value="all">All Statuses</MenuItem>
          <MenuItem value="Pending">Pending</MenuItem>
          <MenuItem value="Completed">Completed</MenuItem>
          <MenuItem value="Failed">Failed</MenuItem>
        </Select>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 1, boxShadow: 0, overflowX: 'auto', border: `1px solid ${MS_COLORS.border}` }}>
        <Table sx={{ minWidth: 900 }}>
          <TableHead sx={{ bgcolor: MS_COLORS.backgroundLight }}>
            <TableRow>
              <TableCell sx={headerCellSx}>Type</TableCell>
              <TableCell sx={headerCellSx}>Organisation</TableCell>
              <TableCell sx={headerCellSx}>Form</TableCell>
              <TableCell sx={headerCellSx}>Applicant</TableCell>
              <TableCell sx={headerCellSx}>Email</TableCell>
              <TableCell sx={headerCellSx}>Category</TableCell>
              <TableCell sx={headerCellSx}>Amount (UGX)</TableCell>
              <TableCell sx={headerCellSx}>Payment Method</TableCell>
              <TableCell sx={headerCellSx}>Date</TableCell>
              <TableCell sx={headerCellSx}>Status</TableCell>
              <TableCell sx={headerCellSx}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4, color: MS_COLORS.textSecondary, fontSize: '0.875rem' }}>
                  No applications found.
                </TableCell>
              </TableRow>
            )}
            {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((app) => (
              <TableRow key={app.id} sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { backgroundColor: MS_COLORS.hoverBackground } }}>
                <TableCell sx={bodyCellSx}><FormTypeChip type={app.form_type} /></TableCell>
                <TableCell sx={bodyCellSx}>{app.organization_name}</TableCell>
                <TableCell sx={bodyCellSx}>{app.form_title}</TableCell>
                <TableCell sx={bodyCellSx}>{app.applicant_name}</TableCell>
                <TableCell sx={bodyCellSx}>{app.applicant_email}</TableCell>
                <TableCell sx={bodyCellSx}>{app.category_name}</TableCell>
                <TableCell sx={bodyCellSx}>UGX {app.total_amount_ugx?.toLocaleString()}</TableCell>
                <TableCell sx={bodyCellSx}>{app.payment_method || '-'}</TableCell>
                <TableCell sx={bodyCellSx}>
                  {app.created_at ? new Date(app.created_at).toLocaleDateString('en-GB') : '-'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', py: 1, whiteSpace: 'nowrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5,
                    color: app.payment_status === 'Completed' ? MS_COLORS.accent
                         : app.payment_status === 'Pending'   ? MS_COLORS.orange
                         : MS_COLORS.red }}>
                    {app.payment_status === 'Completed' ? <CheckCircle fontSize="small" />
                     : app.payment_status === 'Pending'  ? <AccessTime fontSize="small" />
                     : <Cancel fontSize="small" />}
                    <Typography variant="body2">{app.payment_status}</Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => handleViewDetail(app)}>
                      <Visibility fontSize="small" sx={{ color: MS_COLORS.primary }} />
                    </IconButton>
                  </Tooltip>
                  {app.payment_status === 'Failed' && (
                    <Tooltip title="Retry (send retry link to applicant)">
                      <IconButton size="small" onClick={() => handleRetry(app)}>
                        <Replay fontSize="small" sx={{ color: MS_COLORS.orange }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {app.payment_status === 'Pending' && (
                    <Tooltip title="Mark as Failed">
                      <IconButton size="small" onClick={() => handleMarkFailed(app)}>
                        <Block fontSize="small" sx={{ color: MS_COLORS.red }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filtered.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-actions': { fontSize: '0.8rem', color: MS_COLORS.textSecondary } }}
        />
      )}

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '8px', p: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}>
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: MS_COLORS.text }}>
          Application Details
        </DialogTitle>
        <Divider sx={{ mb: 2 }} />
        <DialogContent>
          {selectedApp && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <DetailRow label="Application ID" value={`#${selectedApp.id}`} />
              <DetailRow label="Type" value={selectedApp.form_type === 'event' ? 'Event' : 'Membership'} />
              <DetailRow label="Organisation" value={selectedApp.organization_name} />
              <DetailRow label="Form" value={selectedApp.form_title} />
              <DetailRow label="Category" value={`${selectedApp.category_name} (${selectedApp.billing_cycle})`} />
              <DetailRow label="Status" value={selectedApp.payment_status} />
              <DetailRow label="Applied" value={selectedApp.created_at ? new Date(selectedApp.created_at).toLocaleString() : 'N/A'} />
              {selectedApp.paid_at && <DetailRow label="Paid At" value={new Date(selectedApp.paid_at).toLocaleString()} />}
              <DetailRow label="Reference" value={selectedApp.external_reference || 'N/A'} />
              {selectedApp.transaction_id && <DetailRow label="Transaction ID" value={`#${selectedApp.transaction_id}`} />}

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: MS_COLORS.text }}>Applicant</Typography>
              <DetailRow label="Name" value={selectedApp.applicant_name} />
              <DetailRow label="Email" value={selectedApp.applicant_email} />
              <DetailRow label="Payment Method" value={selectedApp.payment_method || 'N/A'} />

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: MS_COLORS.text }}>Amount</Typography>
              <DetailRow label="Base Amount" value={`UGX ${selectedApp.base_amount?.toLocaleString()}`} />
              {selectedApp.voluntary_amount > 0 && (
                <DetailRow label="Voluntary Contribution" value={`UGX ${selectedApp.voluntary_amount?.toLocaleString()}`} />
              )}
              <DetailRow label="Total" value={`UGX ${selectedApp.total_amount_ugx?.toLocaleString()}`} />

              {selectedApp.field_data && Object.keys(
                typeof selectedApp.field_data === 'string'
                  ? JSON.parse(selectedApp.field_data)
                  : selectedApp.field_data
              ).length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: MS_COLORS.text }}>Custom Fields</Typography>
                  {Object.entries(
                    typeof selectedApp.field_data === 'string'
                      ? JSON.parse(selectedApp.field_data)
                      : selectedApp.field_data
                  ).map(([k, v]) => (
                    <DetailRow key={k} label={k.replace(/_\d+$/, '').replace(/_/g, ' ')} value={Array.isArray(v) ? v.join(', ') : String(v || 'N/A')} />
                  ))}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ pt: 2, px: 3 }}>
          <Button onClick={() => setDetailOpen(false)} variant="outlined"
            sx={{ borderRadius: '4px', fontSize: '0.85rem', textTransform: 'none', borderColor: MS_COLORS.border, color: MS_COLORS.text }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Retry Dialog */}
      <Dialog open={retryOpen} onClose={() => !retryLoading && setRetryOpen(false)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '8px', p: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}>
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: MS_COLORS.text }}>
          Retry Application Payment
        </DialogTitle>
        <Divider sx={{ mb: 2 }} />
        <DialogContent>
          {retryApp && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2, color: MS_COLORS.textSecondary }}>
                This will reset the application to <strong>Pending</strong> and send a retry payment link to{' '}
                <strong>{retryApp.applicant_email}</strong>. The applicant can then complete payment from the form page.
              </Typography>
              <Box sx={{ backgroundColor: MS_COLORS.backgroundLight, p: 2, borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: MS_COLORS.text }}>
                  Amount: UGX {retryApp.total_amount_ugx?.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary, mt: 0.5 }}>
                  Form: {retryApp.form_title}
                </Typography>
                <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary, mt: 0.5 }}>
                  Applicant: {retryApp.applicant_name}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <Divider sx={{ mt: 2 }} />
        <DialogActions sx={{ justifyContent: 'flex-end', pt: 2, px: 3 }}>
          <Button onClick={() => setRetryOpen(false)} disabled={retryLoading} variant="outlined"
            sx={{ borderRadius: '4px', fontSize: '0.85rem', textTransform: 'none', borderColor: MS_COLORS.border, color: MS_COLORS.text }}>
            Cancel
          </Button>
          <Button onClick={handleRetryConfirm} variant="contained" disabled={retryLoading}
            sx={{ textTransform: 'none', borderRadius: '4px', fontSize: '0.85rem', backgroundColor: MS_COLORS.primary,
                  color: MS_COLORS.white, minWidth: 130, '&:hover': { backgroundColor: MS_COLORS.primary, opacity: 0.9 } }}>
            {retryLoading ? <><CircularProgress size={18} sx={{ color: MS_COLORS.white, mr: 1 }} />Sending...</> : 'Send Retry Link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark Failed Dialog */}
      <Dialog open={markFailedOpen} onClose={() => !markFailedLoading && setMarkFailedOpen(false)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '8px', p: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}>
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: MS_COLORS.text }}>
          Mark Application as Failed
        </DialogTitle>
        <Divider sx={{ mb: 2 }} />
        <DialogContent>
          {markFailedApp && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2, color: MS_COLORS.textSecondary }}>
                This will mark the pending application as <strong>Failed</strong>
                {markFailedApp.applicant_email ? ` and notify ${markFailedApp.applicant_email}.` : '.'}
              </Typography>
              <Box sx={{ backgroundColor: MS_COLORS.backgroundLight, p: 2, borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: MS_COLORS.text }}>
                  Amount: UGX {markFailedApp.total_amount_ugx?.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary, mt: 0.5 }}>
                  Form: {markFailedApp.form_title}
                </Typography>
                <Typography variant="body2" sx={{ color: MS_COLORS.textSecondary, mt: 0.5 }}>
                  Applicant: {markFailedApp.applicant_name}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <Divider sx={{ mt: 2 }} />
        <DialogActions sx={{ justifyContent: 'flex-end', pt: 2, px: 3 }}>
          <Button onClick={() => setMarkFailedOpen(false)} disabled={markFailedLoading} variant="outlined"
            sx={{ borderRadius: '4px', fontSize: '0.85rem', textTransform: 'none', borderColor: MS_COLORS.border, color: MS_COLORS.text }}>
            Cancel
          </Button>
          <Button onClick={handleMarkFailedConfirm} variant="contained" disabled={markFailedLoading}
            sx={{ textTransform: 'none', borderRadius: '4px', fontSize: '0.85rem', backgroundColor: MS_COLORS.red,
                  color: MS_COLORS.white, minWidth: 140, '&:hover': { backgroundColor: MS_COLORS.red, opacity: 0.9 } }}>
            {markFailedLoading ? <><CircularProgress size={18} sx={{ color: MS_COLORS.white, mr: 1 }} />Processing...</> : 'Mark as Failed'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageMembershipTransactions;
